import { makeAutoObservable, runInAction } from "mobx";
import RootStore from "stores/RootStore";
import ServiceStore from "stores/ServiceStore";

interface InventoryCheckRecord {
  id?: number;
  checkNumber: string;
  checkDate: Date;
  performedBy: number | null;
  shift: "day" | "night" | "swing";

  // Materials Checked
  materials: {
    materialId: number;
    materialName: string;
    previousQuantity: number;
    currentQuantity: number;
    difference: number;
    unit: string;
    binLocation: string;
    needsReorder: boolean;
    notes: string;
  }[];

  // Overall Status
  discrepanciesFound: boolean;
  actionRequired: boolean;
  actionTaken: string;
  notes: string;
  status: "draft" | "submitted" | "reviewed" | "approved";
}

class PlantInventoryCheckWorkflowStore {
  rootStore: RootStore;

  loading = false;
  error: string | null = null;
  success: string | null = null;
  activeStep = 0;
  checkData: InventoryCheckRecord;

  constructor(rootStore: RootStore) {
    this.checkData = this.getDefaultCheck();
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false });
  }

  getDefaultCheck = (): InventoryCheckRecord => ({
    checkNumber: this.generateCheckNumber(),
    checkDate: new Date(),
    performedBy: null,
    shift: "day",
    materials: [],
    discrepanciesFound: false,
    actionRequired: false,
    actionTaken: "",
    notes: "",
    status: "draft",
  });

  generateCheckNumber = (): string => {
    const now = new Date();
    const year = now.getFullYear().toString().slice(-2);
    const month = (now.getMonth() + 1).toString().padStart(2, "0");
    const day = now.getDate().toString().padStart(2, "0");
    return `INV${year}${month}${day}-${Math.random().toString(36).substr(2, 4).toUpperCase()}`;
  };

  setLoading = (loading: boolean) => {
    this.loading = loading;
  };

  setError = (error: string | null) => {
    this.error = error;
  };

  setSuccess = (success: string | null) => {
    this.success = success;
  };

  setActiveStep = (step: number) => {
    this.activeStep = step;
  };

  nextStep = () => {
    if (this.validateCurrentStep()) {
      this.activeStep++;
    }
  };

  prevStep = () => {
    this.activeStep--;
  };

  updateCheckData = <K extends keyof InventoryCheckRecord>(
    field: K,
    value: InventoryCheckRecord[K]
  ) => {
    this.checkData[field] = value;
  };

  validateCurrentStep = (): boolean => {
    this.setError(null);
    if (this.activeStep === 0 && !this.checkData.performedBy) {
      this.setError("Please specify who performed this check");
      return false;
    }
    return true;
  };

  loadInitialData = async () => {
    this.setLoading(true);
    try {
      const materialsStore = this.rootStore.materialsStore as ServiceStore;
      await materialsStore?.find();
    } catch (err: any) {
      runInAction(() => this.setError("Failed to load data"));
    } finally {
      runInAction(() => this.setLoading(false));
    }
  };

  submitCheck = async () => {
    this.setLoading(true);
    try {
      runInAction(() => {
        this.setSuccess("Inventory check submitted successfully!");
        this.activeStep = 2;
      });
    } catch (err: any) {
      runInAction(() => this.setError(err.message || "Failed to submit"));
    } finally {
      runInAction(() => this.setLoading(false));
    }
  };

  reset = () => {
    this.loading = false;
    this.error = null;
    this.success = null;
    this.activeStep = 0;
    this.checkData = this.getDefaultCheck();
  };

  get materials() {
    const materialsStore = this.rootStore.materialsStore as ServiceStore;
    return materialsStore?.entities || [];
  }
}

export default PlantInventoryCheckWorkflowStore;
